import { GetSpotifyArtistSearchResponse, spotifyQueries } from '@round/api';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { getSpotifyArtistUrl, mapToItem, SpotifyArtistItem } from './useSpotifyArtistCombobox';
import { useSpotifyArtists } from '@round/platforms/spotify';
import { uniqBy } from 'lodash';

const artistsQueryKey = spotifyQueries.artistSearch.keys.lists;

export function useSelectedSpotifyArtists(selectedArtistSpotifyIds: string[]) {
    const queryClient = useQueryClient();

    // The search cache holds full Spotify-API metadata (genres, followers, image_url).
    // Use it as the primary source to avoid extra DB calls for recently searched artists,
    // and to handle the window before the persisting task has saved the artist to our DB.
    const artistQueryCacheData = queryClient
        .getQueriesData<InfiniteData<GetSpotifyArtistSearchResponse['data']> | GetSpotifyArtistSearchResponse['data']>({
            queryKey: artistsQueryKey(),
        })
        .map((cacheEntry) => cacheEntry[1]);

    const artistsInCache = artistQueryCacheData
        .map((data) => {
            if (!data) return [];
            if ('pages' in data) return data.pages.flatMap((p) => p.results);
            return data.results;
        })
        .flat();

    const relevantArtistsInCache = artistsInCache.filter((a) => selectedArtistSpotifyIds.includes(a.spotify_id));
    const relevantArtistsInCacheIds = relevantArtistsInCache.map((a) => a.spotify_id);

    const artistsNotInCacheIds = selectedArtistSpotifyIds.filter((id) => !relevantArtistsInCacheIds.includes(id));

    const { data: dbArtistsData, isFetching: isDbFetching } = useSpotifyArtists(
        { spotify_id: artistsNotInCacheIds.join(','), page_size: artistsNotInCacheIds.length },
        { enabled: artistsNotInCacheIds.length > 0 }
    );
    const dbArtists = dbArtistsData?.results ?? [];

    const items: SpotifyArtistItem[] = uniqBy(
        [
            ...relevantArtistsInCache.map(mapToItem),
            ...dbArtists.map((artist) => ({
                value: artist.spotify_id,
                label: artist.name,
                spotify_url: getSpotifyArtistUrl(artist.spotify_id),
                image_url: null,
                genres: [] as string[],
            })),
        ],
        'value'
    );

    return {
        items,
        isFetching: isDbFetching,
    };
}
